import React, { Fragment, useCallback, useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";
import {
    BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer,
    PieChart, Pie, Cell, AreaChart, Area, CartesianGrid,
} from "recharts";
import Breadcrumb from "../common/breadcrumb";
import { Container } from "reactstrap";
import { C, tbl, PIE_COLORS, reportingGlobalCSS } from "./reportingStyles";
import { GradientDefs, ChartTooltip, Tile, RCard, Badge, Loader } from "./ReportingShared";
import { getMembershipAnalytics } from "../../utils/membershipApi";
import { exportToCsv } from "../../utils/exportCsv";
import {
    STATUS_LABELS, BLOCK_REASON_LABELS, formatDiscount,
} from "../membership/membershipConstants";

/**
 * Membership, in aggregate. The mirror of CustomerLoyaltyPage for the other programme.
 *
 * Every money figure here is net of returns — the API sums a signed ledger rather than
 * `Order.membershipDiscount`, so a refunded order stops counting. Worth knowing before
 * anyone reconciles this page against an orders export and finds a gap: the gap is the
 * returns, and this page is the one that is right.
 *
 * The refusal-reasons table is the most actionable thing on the page and the reason the
 * scan log exists at all: a refused scan produces no order and no ledger row, so a spike
 * in `usage_limit` (caps too tight) or `membership_expired` (nobody renewing) is visible
 * nowhere else.
 */

const ghostButton = {
    padding: "8px 14px", borderRadius: 8, fontSize: 12, fontWeight: 600,
    border: `1px solid ${C.border}`, cursor: "pointer", color: C.textMed, background: C.white,
};

const money = (n) => `SAR ${Number(n || 0).toFixed(2)}`;

const CHANNEL_LABELS = {
    normal: "Pickup App",
    loyalty: "POS",
    waiter: "Waiter",
    kiosk: "Kiosk",
    thirdParty: "Third-party",
    unknown: "Unknown",
};

const MembershipAnalyticsPage = () => {
    const navigate = useNavigate();
    const userType = localStorage.getItem("userType");
    const merchantIdFromStorage = localStorage.getItem("MerchantId");
    const { selectedMerchant: reduxSelectedMerchant } = useSelector((state) => state.merchant || {});
    const selectedMerchant = userType === "merchant" ? merchantIdFromStorage : reduxSelectedMerchant;

    const [loading, setLoading] = useState(false);
    const [data, setData] = useState(null);
    const [days, setDays] = useState(30);

    const fetchData = useCallback(async () => {
        if (!selectedMerchant || selectedMerchant === "all") { setData(null); return; }
        setLoading(true);
        setData(await getMembershipAnalytics(selectedMerchant, { days }));
        setLoading(false);
    }, [selectedMerchant, days]);

    useEffect(() => { fetchData(); }, [fetchData]);

    const statusPie = useMemo(() => {
        const byStatus = data?.summary?.membersByStatus || {};
        return Object.entries(byStatus).map(([status, count]) => ({
            name: STATUS_LABELS[status] || status,
            value: count,
        }));
    }, [data]);

    const tierBars = useMemo(
        () => (data?.byTier || [])
            .filter((t) => t.discountGiven > 0 || t.activeMembers > 0)
            .map((t) => ({ name: t.name, discount: t.discountGiven, members: t.activeMembers })),
        [data]
    );

    const exportTiers = () => {
        exportToCsv(`membership-by-tier-${days}d.csv`, data?.byTier || [], [
            { key: "name", label: "Tier" },
            { key: (t) => formatDiscount(t.discountType, t.discountValue), label: "Terms" },
            { key: (t) => (t.isActive ? "Active" : "Inactive"), label: "Status" },
            { key: "activeMembers", label: "Active members" },
            { key: "membersWhoUsedIt", label: "Members who used it" },
            { key: "discountedOrders", label: "Discounted orders" },
            { key: (t) => t.discountGiven.toFixed(2), label: "Discount given (net of returns)" },
        ]);
    };

    if (!selectedMerchant || selectedMerchant === "all") {
        return (
            <Fragment>
                <Breadcrumb title="Membership Analytics" parent="Reporting" />
                <Container fluid>
                    <style>{reportingGlobalCSS}</style>
                    <div className="rpt-wrap">
                        <RCard>
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                Select a merchant to see its membership analytics.
                            </div>
                        </RCard>
                    </div>
                </Container>
            </Fragment>
        );
    }

    if (loading || !data) {
        return (
            <Fragment>
                <Breadcrumb title="Membership Analytics" parent="Reporting" />
                <Container fluid>
                    <style>{reportingGlobalCSS}</style>
                    <div className="rpt-wrap"><Loader /></div>
                </Container>
            </Fragment>
        );
    }

    const s = data.summary;
    const takeUp = s.totalMembers > 0 ? Math.round((s.membersWhoUsedIt / s.totalMembers) * 100) : 0;

    return (
        <Fragment>
            <Breadcrumb title="Membership Analytics" parent="Reporting" />
            <Container fluid>
                <style>{reportingGlobalCSS}</style>
                <div className="rpt-wrap">

                    {!data.programmeEnabled && (
                        <div style={{
                            padding: "12px 16px", marginBottom: 16, borderRadius: 10,
                            background: C.orangeLight, color: C.text, fontSize: 13,
                        }}>
                            <strong>Membership is switched off for this merchant.</strong> No new discounts are
                            being applied. The figures below are history.
                        </div>
                    )}

                    <div style={{ display: "flex", justifyContent: "flex-end", gap: 8, marginBottom: 16 }}>
                        {[7, 30, 90].map((d) => (
                            <button
                                key={d}
                                onClick={() => setDays(d)}
                                style={{
                                    ...ghostButton,
                                    borderColor: days === d ? C.coral : C.border,
                                    color: days === d ? C.coralDark : C.textMed,
                                    background: days === d ? C.coralLight : C.white,
                                }}
                            >
                                Last {d} days
                            </button>
                        ))}
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(220px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <Tile
                            label="Discount Given"
                            value={money(s.discountGiven)}
                            sub={`${money(s.discountGivenNet)} excl. VAT · net of returns`}
                            icon="💰"
                            gradient={`linear-gradient(135deg, ${C.coral}, #00bfa5)`}
                            delay={0}
                        />
                        <Tile
                            label="Discounted Orders"
                            value={s.discountedOrders}
                            sub={s.discountedOrders > 0 ? `${money(s.discountGiven / s.discountedOrders)} average` : "None yet"}
                            icon="🧾"
                            gradient={`linear-gradient(135deg, ${C.blue}, #A8B8FF)`}
                            delay={80}
                        />
                        <Tile
                            label="Members"
                            value={s.totalMembers}
                            sub={`${s.membersByStatus?.active || 0} active · ${s.activeTiers} of ${s.tiers} tiers live`}
                            icon="👥"
                            gradient={`linear-gradient(135deg, ${C.purple}, #C89BFF)`}
                            delay={160}
                        />
                        <Tile
                            label="Used Their Card"
                            value={`${s.membersWhoUsedIt}`}
                            sub={`${takeUp}% of members, in this window`}
                            icon="📈"
                            gradient={`linear-gradient(135deg, ${C.green}, #38F9D7)`}
                            delay={240}
                        />
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(400px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <RCard title="📉 Discount Given Per Day">
                            <ResponsiveContainer width="100%" height={220}>
                                <AreaChart data={data.trend}>
                                    <GradientDefs />
                                    <CartesianGrid strokeDasharray="3 3" stroke="#F1F5F9" />
                                    <XAxis dataKey="day" tick={{ fontSize: 11, fill: C.textMed }} axisLine={{ stroke: C.border }} tickLine={false} />
                                    <YAxis tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                                    <Tooltip content={<ChartTooltip />} />
                                    <Area type="monotone" dataKey="discount" stroke="#009689" fill="url(#gCoral)" strokeWidth={2} name="SAR" />
                                </AreaChart>
                            </ResponsiveContainer>
                        </RCard>
                        <RCard title="🎖️ Discount By Tier (SAR)">
                            {tierBars.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                    No tier has been used in this window.
                                </div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <BarChart data={tierBars} layout="vertical">
                                        <GradientDefs />
                                        <XAxis type="number" tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                                        <YAxis dataKey="name" type="category" width={100} tick={{ fontSize: 11, fill: C.textMed }} axisLine={false} tickLine={false} />
                                        <Tooltip content={<ChartTooltip />} />
                                        <Bar dataKey="discount" fill="url(#gCoralBar)" radius={[0, 8, 8, 0]} name="SAR" barSize={20} />
                                    </BarChart>
                                </ResponsiveContainer>
                            )}
                        </RCard>
                    </div>

                    <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(340px, 1fr))", gap: 16, marginBottom: 20 }}>
                        <RCard title="👥 Members By Status">
                            {statusPie.length === 0 ? (
                                <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>No members yet.</div>
                            ) : (
                                <ResponsiveContainer width="100%" height={220}>
                                    <PieChart>
                                        <Pie data={statusPie} dataKey="value" nameKey="name" cx="50%" cy="50%" outerRadius={80} label>
                                            {statusPie.map((entry, i) => (
                                                <Cell key={entry.name} fill={PIE_COLORS[i % PIE_COLORS.length]} />
                                            ))}
                                        </Pie>
                                        <Tooltip content={<ChartTooltip />} />
                                    </PieChart>
                                </ResponsiveContainer>
                            )}
                        </RCard>

                        <RCard title="📱 Discounted Orders By Channel">
                            {(data.byChannel || []).length === 0 ? (
                                <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                    No discounted orders in this window.
                                </div>
                            ) : (
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Channel</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Orders</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byChannel.map((c) => (
                                            <tr key={c.channel} className="rpt-row">
                                                <td style={{ ...tbl.td, color: C.text }}>{CHANNEL_LABELS[c.channel] || c.channel}</td>
                                                <td style={{ ...tbl.td, textAlign: "right", fontWeight: 600 }}>{c.orders}</td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            )}
                        </RCard>

                        <RCard title="📷 Counter Scans">
                            {data.scans.total === 0 ? (
                                <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                    {/* Not "0% refused" — no scans at all is a different fact. */}
                                    No cards have been scanned at the counter in this window.
                                </div>
                            ) : (
                                <>
                                    <div style={{ display: "flex", gap: 16, marginBottom: 14 }}>
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{data.scans.accepted}</div>
                                            <div style={{ fontSize: 11, color: C.textMed }}>Accepted</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: C.coral }}>{data.scans.refused}</div>
                                            <div style={{ fontSize: 11, color: C.textMed }}>Refused</div>
                                        </div>
                                        <div>
                                            <div style={{ fontSize: 22, fontWeight: 700, color: C.text }}>{data.scans.refusalRate}%</div>
                                            <div style={{ fontSize: 11, color: C.textMed }}>Refusal rate</div>
                                        </div>
                                    </div>
                                    {(data.scans.refusalReasons || []).length > 0 && (
                                        <table style={tbl.table}>
                                            <thead>
                                                <tr>
                                                    <th style={tbl.th}>Why refused</th>
                                                    <th style={{ ...tbl.th, textAlign: "right" }}>Count</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {data.scans.refusalReasons.map((r) => (
                                                    <tr key={r.reason} className="rpt-row">
                                                        <td style={{ ...tbl.td, color: C.text, fontSize: 12 }}>
                                                            {BLOCK_REASON_LABELS[r.reason] || r.reason}
                                                        </td>
                                                        <td style={{ ...tbl.td, textAlign: "right", fontWeight: 600 }}>{r.count}</td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </table>
                                    )}
                                </>
                            )}
                        </RCard>
                    </div>

                    <RCard
                        title={
                            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", width: "100%" }}>
                                <span>🎖️ By Tier</span>
                                <button onClick={exportTiers} style={ghostButton} disabled={!(data.byTier || []).length}>
                                    ⬇ Export
                                </button>
                            </div>
                        }
                    >
                        {(data.byTier || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                No tiers configured. <span style={{ color: C.coral, cursor: "pointer" }} onClick={() => navigate("/membership/types")}>Create one →</span>
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Tier</th>
                                            <th style={tbl.th}>Terms</th>
                                            <th style={tbl.th}>Status</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Active Members</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Used It</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Orders</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Discount Given</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.byTier.map((t) => (
                                            <tr key={t.id} className="rpt-row">
                                                <td style={{ ...tbl.td, fontWeight: 600, color: C.text }}>
                                                    <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                                                        <span style={{
                                                            width: 10, height: 10, borderRadius: 3, flexShrink: 0,
                                                            background: t.cardColour || C.textLight,
                                                        }} />
                                                        <span
                                                            style={{ cursor: "pointer" }}
                                                            onClick={() => navigate(`/membership/types/edit/${t.id}`)}
                                                        >
                                                            {t.name}
                                                        </span>
                                                    </div>
                                                </td>
                                                <td style={tbl.td}>{formatDiscount(t.discountType, t.discountValue)}</td>
                                                <td style={tbl.td}>
                                                    {t.isActive
                                                        ? <Badge color={C.green} bg={C.greenLight}>● Active</Badge>
                                                        : <Badge color={C.coral} bg={C.coralLight}>● Inactive</Badge>}
                                                </td>
                                                <td style={{ ...tbl.td, textAlign: "right" }}>{t.activeMembers}</td>
                                                <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{t.membersWhoUsedIt}</td>
                                                <td style={{ ...tbl.td, textAlign: "right", color: C.textMed }}>{t.discountedOrders}</td>
                                                <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: C.text }}>
                                                    {money(t.discountGiven)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </RCard>

                    <RCard title="🏆 Top Members">
                        {(data.topMembers || []).length === 0 ? (
                            <div style={{ textAlign: "center", padding: 40, color: C.textMed }}>
                                {/* A member whose orders were all returned nets to zero and is
                                    excluded by the API, which is why this can be empty even with
                                    ledger activity in the window. */}
                                No member has kept a discount in this window.
                            </div>
                        ) : (
                            <div style={{ overflowX: "auto" }}>
                                <table style={tbl.table}>
                                    <thead>
                                        <tr>
                                            <th style={tbl.th}>Card</th>
                                            <th style={tbl.th}>Member</th>
                                            <th style={tbl.th}>Tier</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Orders</th>
                                            <th style={{ ...tbl.th, textAlign: "right" }}>Discount</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {data.topMembers.map((m) => (
                                            <tr key={m.membershipId} className="rpt-row">
                                                <td
                                                    style={{ ...tbl.td, fontFamily: "monospace", fontWeight: 600, color: C.coral, cursor: "pointer" }}
                                                    onClick={() => navigate(`/membership/members/${m.membershipId}`)}
                                                >
                                                    {m.membershipRef || `#${m.membershipId}`}
                                                </td>
                                                <td style={tbl.td}>
                                                    <div style={{ color: C.text }}>{m.name || "—"}</div>
                                                    <div style={{ fontSize: 11, color: C.textLight }}>{m.phone || "—"}</div>
                                                </td>
                                                <td style={{ ...tbl.td, color: C.textMed }}>{m.tierName || "—"}</td>
                                                <td style={{ ...tbl.td, textAlign: "right" }}>{m.orders}</td>
                                                <td style={{ ...tbl.td, textAlign: "right", fontWeight: 700, color: C.text }}>
                                                    {money(m.discountGiven)}
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                        )}
                    </RCard>
                </div>
            </Container>
        </Fragment>
    );
};

export default MembershipAnalyticsPage;
